/**
 * Mount a playhead overlay on a container. The overlay is a thin vertical
 * line positioned by percentage based on getPositionSec() / totalDurationSec.
 *
 * RAF-based — calls getPositionSec each frame while running. Caller controls
 * start/stop via returned handle.
 *
 * @param {HTMLElement} host  must be position:relative or absolute
 * @param {object} opts
 * @param {() => number} opts.getPositionSec  callback returning current playhead time
 * @param {() => number} opts.getTotalDurationSec  callback returning song duration
 * @param {(sec: number) => void} [opts.onClickSeek]  click-to-seek handler. Receives target sec.
 * @returns {{ start: () => void, stop: () => void, dispose: () => void, element: HTMLElement }}
 */
export function mountPlayheadOverlay(host, opts) {
  const { getPositionSec, getTotalDurationSec, onClickSeek } = opts;
  const el = document.createElement('div');
  el.className = 'rh-playhead';
  host.appendChild(el);

  let rafId = 0;
  let running = false;
  const tick = () => {
    if (!running) return;
    const sec = getPositionSec();
    const total = getTotalDurationSec();
    if (total > 0 && Number.isFinite(sec)) {
      const pct = Math.max(0, Math.min(100, (sec / total) * 100));
      el.style.left = `${pct}%`;
    }
    rafId = requestAnimationFrame(tick);
  };

  let clickHandler = null;
  if (onClickSeek) {
    clickHandler = (ev) => {
      const rect = host.getBoundingClientRect();
      const x = ev.clientX - rect.left;
      const total = getTotalDurationSec();
      if (total > 0 && rect.width > 0) {
        const sec = (x / rect.width) * total;
        onClickSeek(sec);
      }
    };
    host.addEventListener('click', clickHandler);
  }

  return {
    element: el,
    start() {
      if (running) return;
      running = true;
      rafId = requestAnimationFrame(tick);
    },
    stop() {
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = 0;
    },
    dispose() {
      this.stop();
      if (clickHandler) host.removeEventListener('click', clickHandler);
      if (el.parentElement === host) host.removeChild(el);
    },
  };
}
